import { X, Plus, Minus, Trash2, Gift } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';

export const CartDrawer = () => {
  const { drawerOpen, setDrawerOpen, items, updateQty, removeItem, subtotal, bundleDiscount, freeShipThreshold, selectedGift, setGiftPickerOpen } = useCart();
  const { format } = useCurrency();
  const remaining = Math.max(0, freeShipThreshold - (subtotal - bundleDiscount));
  const progress = Math.min(100, ((subtotal - bundleDiscount) / freeShipThreshold) * 100);

  return (
    <>
      <div
        className={`fixed inset-0 z-50 bg-lum-text/40 transition-opacity duration-300 ${drawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setDrawerOpen(false)}
      />
      <aside
        data-testid="cart-drawer"
        className={`fixed right-0 top-0 z-50 h-full w-full sm:w-[440px] bg-lum-bg shadow-2xl transition-transform duration-500 ease-out flex flex-col ${drawerOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-lum-border">
          <p className="overline">Your Bag ({items.length})</p>
          <button data-testid="cart-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="close"><X size={20} /></button>
        </div>

        <div className="px-6 py-4 border-b border-lum-border">
          {remaining > 0 ? (
            <p className="text-sm text-lum-text2">You&apos;re <span className="text-lum-text font-medium">{format(remaining)}</span> away from free shipping.</p>
          ) : (
            <p className="text-sm text-lum-text">✓ Free shipping unlocked.</p>
          )}
          <div className="h-[2px] bg-lum-bg3 mt-3">
            <div className="h-full bg-lum-text transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-serif-display text-3xl mb-2">Your bag is quiet.</p>
              <p className="text-sm text-lum-text2 mb-6">Time to choose your ritual.</p>
              <Link to="/shop" onClick={() => setDrawerOpen(false)} data-testid="drawer-shop-link" className="btn-primary">Shop Masks</Link>
            </div>
          ) : (
            <ul className="space-y-6">
              {items.map((i) => (
                <li key={i.key} data-testid={`cart-item-${i.product_id}-${i.pack_size}`} className="flex gap-4">
                  <div className={`w-24 h-28 ${i.accent === 'blue' ? 'accent-blue-soft' : i.accent === 'green' ? 'accent-green-soft' : 'accent-orange-soft'} flex-shrink-0`}>
                    <img src={i.image} alt={i.name} className="w-full h-full object-contain" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between gap-2">
                      <h4 className="font-serif-display text-xl leading-tight">{i.name}</h4>
                      <button data-testid={`cart-remove-${i.product_id}`} onClick={() => removeItem(i.key)} aria-label="remove"><Trash2 size={16} className="text-lum-muted hover:text-lum-text" /></button>
                    </div>
                    <p className="text-xs text-lum-muted mt-1">{i.pack_size === 'pack' ? `Pack of ${i.pack_count}` : 'Single sheet'}</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-lum-border">
                        <button onClick={() => updateQty(i.key, i.quantity - 1)} className="px-2 py-1"><Minus size={12} /></button>
                        <span className="px-3 text-sm">{i.quantity}</span>
                        <button data-testid={`cart-qty-plus-${i.product_id}`} onClick={() => updateQty(i.key, i.quantity + 1)} className="px-2 py-1"><Plus size={12} /></button>
                      </div>
                      <p className="text-sm">{format(i.unit_price * i.quantity)}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-lum-border px-6 py-5 space-y-3">
            {selectedGift ? (
              <div data-testid="cart-selected-gift" className="flex items-center gap-3 p-3 bg-lum-bg2 border border-lum-border">
                <div className={`w-12 h-12 ${selectedGift.accent === 'green' ? 'accent-green-soft' : 'accent-orange-soft'} flex-shrink-0`}>
                  <img src={selectedGift.image} alt={selectedGift.name} className="w-full h-full object-contain p-1" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-lum-sale">Your free gift</p>
                  <p className="text-sm leading-tight truncate">{selectedGift.name}</p>
                </div>
                <button data-testid="cart-change-gift" onClick={() => setGiftPickerOpen(true)} className="text-xs uppercase tracking-[0.18em] underline underline-offset-4">Change</button>
              </div>
            ) : (
              <button
                data-testid="cart-pick-gift-button"
                onClick={() => setGiftPickerOpen(true)}
                className="w-full flex items-center justify-between gap-3 p-3 border border-dashed border-lum-text bg-lum-bg2/60 hover:bg-lum-bg2 transition-colors"
              >
                <span className="flex items-center gap-2 text-sm">
                  <Gift size={16} className="text-lum-sale" />
                  Pick your free gift
                </span>
                <span className="text-xs uppercase tracking-[0.18em] text-lum-sale">Choose →</span>
              </button>
            )}
            <div className="flex justify-between text-sm"><span className="text-lum-text2">Subtotal</span><span>{format(subtotal)}</span></div>
            {bundleDiscount > 0 && (
              <div className="flex justify-between text-sm text-lum-sale"><span>Bundle discount (10%)</span><span>−{format(bundleDiscount)}</span></div>
            )}
            <Link to="/checkout" onClick={() => setDrawerOpen(false)} data-testid="drawer-checkout-button" className="btn-primary w-full">Checkout</Link>
            <Link to="/cart" onClick={() => setDrawerOpen(false)} className="btn-ghost block text-center">View full cart</Link>
          </div>
        )}
      </aside>
    </>
  );
};
